import { airlineName } from "../airline-names";
import { escapeHtml } from "../escape";
import { findFlight } from "../find-flight";
import { $flights, $pinned, $planes, $selectedId } from "../store";

function badge(status: string | null) {
  const s = (status ?? "").toLowerCase();
  if (s === "landed")
    return `<span class="badge landed"><i class="ph-fill ph-check-circle"></i>Landed</span>`;
  if (s === "active")
    return `<span class="badge active"><i class="ph-fill ph-airplane-tilt"></i>Active</span>`;
  if (s === "scheduled") return `<span class="badge scheduled"><i class="ph ph-clock"></i>Scheduled</span>`;
  return `<span class="badge">${escapeHtml(s) || "Unknown"}</span>`;
}

function hmm(t: string | null) {
  if (!t) return "—";
  // expect "2026-04-09 15:23"
  const m = /(\d{2}:\d{2})/.exec(t);
  // Fall back to the raw value when it isn't an HH:MM — escape it since the
  // time string is upstream-controlled (AirLabs) and rendered into innerHTML.
  return m ? m[1]! : escapeHtml(t);
}

export function renderDetail(host: HTMLElement) {
  host.innerHTML = `<div class="card detail"></div>`;
  const card = host.querySelector(".detail")!;

  function render() {
    const selId = $selectedId.get();
    const planes = $planes.get();
    const plane = selId ? planes[selId] : null;
    const flights = $flights.get();
    // Look up the flight by callsign, not by selectedId. The id is the
    // plane's hex (Pi adsb-streamer / dump1090 schema) — only the mock
    // feed coincidentally used the callsign as id, which masked this bug.
    const flight = findFlight(plane?.call ?? selId, flights);

    if (!plane && !flight) {
      card.classList.add("empty");
      card.innerHTML = `
        <div class="head"><div class="l"><i class="ph ph-airplane" style="color:var(--muted)"></i><span class="title">No selection</span></div></div>
        <div class="divider"></div>
        <div style="padding:16px;color:var(--muted);font-size:var(--fs-xs)">Tap a plane on the map or in the list to inspect.</div>
      `;
      return;
    }
    card.classList.remove("empty");

    const call = flight?.flight_iata ?? plane?.call ?? "—";
    const ica = flight?.flight_icao ?? "";
    const dep = flight?.dep_iata ?? "—";
    const depIcao = flight?.dep_icao ?? "";
    const arr = flight?.arr_iata ?? "—";
    const arrIcao = flight?.arr_icao ?? "";
    const dur = flight?.duration ? `${flight.duration} min` : "—";
    const isDelay = (flight?.delayed ?? "0") !== null && parseFloat(flight?.delayed ?? "0") > 0;
    const arrDelay = parseFloat(flight?.arr_delayed ?? "0");
    const depDelay = parseFloat(flight?.dep_delayed ?? "0");
    // Prefer the flight's airline_icao + lookup (the airline operating THIS
    // leg), then the aircraft's registered owner, then the raw ICAO, then "—".
    const operator = airlineName(flight?.airline_icao) || plane?.operator || flight?.airline_icao || "—";
    const aircraft = flight?.aircraft_icao || plane?.type || "—";

    // Every interpolated field below comes from the ADS-B feed or the
    // AirLabs/aircraft lookup (untrusted upstream) and is escaped for its
    // element-body or attribute context. Numeric fields (alt/spd/delay
    // minutes) are coerced via parseFloat/toLocaleString; hmm() escapes its
    // own raw fallback. See SCALING_AUDIT.md B-07.
    card.innerHTML = `
      <div class="head">
        <div class="l">
          <i class="ph-fill ph-airplane" style="color:var(--accent-amber);font-size:var(--fs-md)"></i>
          <span class="title">${escapeHtml(call)} ${ica ? `<span style="color:var(--muted);font-size:var(--fs-xs);font-weight:500;margin-left:6px">${escapeHtml(ica)}</span>` : ""}</span>
        </div>
        <div class="r"><button class="iconbtn pin-btn ${$pinned.get() ? "on" : ""}" title="${$pinned.get() ? "Unpin selection" : "Pin selection"}" aria-pressed="${$pinned.get()}"><i class="ph${$pinned.get() ? "-fill" : ""} ph-push-pin"></i></button></div>
      </div>
      <div class="divider"></div>
      <div class="badges">
        ${badge(flight?.status ?? null)}
        ${isDelay ? `<span class="badge"><i class="ph ph-clock"></i>+${parseFloat(flight?.delayed ?? "0")} min</span>` : ""}
        ${flight?.duration ? `<span class="badge"><i class="ph-fill ph-airplane-tilt"></i>${escapeHtml(dur)}</span>` : ""}
      </div>
      <div class="route">
        <div class="ap">
          <div class="code">${escapeHtml(dep)}</div>
          <div class="meta">${escapeHtml(depIcao)}${flight?.dep_gate ? ` · Gate ${escapeHtml(flight.dep_gate)}` : ""}${flight?.dep_terminal ? ` · T${escapeHtml(flight.dep_terminal)}` : ""}</div>
          <div class="time">${hmm(flight?.dep_estimated ?? flight?.dep_time ?? null)}<span class="zone">JST</span></div>
        </div>
        <div class="arrow">
          <span class="dur">${escapeHtml(dur)}</span>
          <div class="line"></div>
          <i class="ph-fill ph-airplane"></i>
        </div>
        <div class="ap right">
          <div class="code">${escapeHtml(arr)}</div>
          <div class="meta">${escapeHtml(arrIcao)}${flight?.arr_terminal ? ` · T${escapeHtml(flight.arr_terminal)}` : ""}</div>
          <div class="time">${hmm(flight?.arr_estimated ?? flight?.arr_time ?? null)}<span class="zone">JST</span></div>
        </div>
      </div>
      <div class="divider"></div>
      <div class="grid-info">
        <div class="cell"><span class="k">Aircraft</span><span class="v">${escapeHtml(aircraft)}${plane?.manufacturer ? `<span class="sub" style="color:var(--muted);font-weight:500;margin-left:4px">${escapeHtml(plane.manufacturer)}</span>` : ""}</span></div>
        <div class="cell"><span class="k">Registration</span><span class="v">${escapeHtml(plane?.registration || "—")}</span></div>
        <div class="cell"><span class="k">Operator</span><span class="v">${escapeHtml(operator)}</span></div>
        <div class="cell"><span class="k">Squawk</span><span class="v">${escapeHtml(plane?.squawk || "—")}</span></div>
        <div class="cell"><span class="k">Sched dep</span><span class="v">${hmm(flight?.dep_time ?? null)}</span></div>
        <div class="cell"><span class="k">Est dep</span><span class="v ${depDelay > 0 ? "delayed" : ""}">${hmm(flight?.dep_estimated ?? null)}</span></div>
        <div class="cell"><span class="k">Sched arr</span><span class="v">${hmm(flight?.arr_time ?? null)}</span></div>
        <div class="cell"><span class="k">Est arr</span><span class="v ${arrDelay > 0 ? "delayed" : ""}">${hmm(flight?.arr_estimated ?? null)}</span></div>
        <div class="cell"><span class="k">Baggage</span><span class="v">${escapeHtml(flight?.arr_baggage || "—")}</span></div>
        <div class="cell"><span class="k">Altitude</span><span class="v">${plane?.alt ? plane.alt.toLocaleString() + " ft" : "—"}</span></div>
      </div>
      ${flight?.cs_flight_iata ? `<div class="codeshare"><i class="ph ph-arrows-merge"></i>Codeshare<b>${escapeHtml(flight.flight_iata ?? "")} · ${escapeHtml(flight.cs_flight_iata)}</b></div>` : ""}
    `;
  }

  card.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest(".pin-btn");
    if (btn) {
      $pinned.set(!$pinned.get());
      render();
    }
  });

  $selectedId.subscribe(render);
  $planes.subscribe(render);
  $flights.subscribe(render);
  $pinned.subscribe(render);
}
